/**
 * Assist as the phone's assistant: the sheet the side key or the corner swipe
 * opens over whatever the person was using (docs/android-bridge.md).
 *
 * It starts listening at once, in a temporary chat, so a quick question
 * leaves nothing behind. The screen underneath can go with the question when
 * the person adds it. "Open in Assist" keeps the chat and takes it to the
 * full app; closing the sheet deletes it.
 *
 * Drawn on a transparent page: Android dims the app behind, and a tap on that
 * closes the sheet. In a browser the same page works as a plain sheet, typed
 * rather than spoken, since audio there needs a click first.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ApiError,
  createChat,
  deleteChat,
  getChat,
  patchChat,
  uploadFile,
  type Attachment,
  type ChatSummary,
  type Message
} from '../api';
import {
  captureFiles,
  closeSheet,
  hello,
  inAndroid,
  onScreen,
  openInApp,
  screenCapture,
  type ScreenOffer
} from '../android';
import { toSummary } from '../hooks/chatStash';
import { markSaved, markTemporary } from '../hooks/temporaryChats';
import { startTurn, stopTurn, useLiveTurn, withTurn } from '../hooks/useTurns';
import { useRouter } from '../router';
import { useApp } from '../state';
import { Markdown } from '../components/Markdown';
import { Ph } from '../components/ui';
import { VoiceMode } from '../components/voice/VoiceMode';

type ScreenState = 'offered' | 'adding' | 'added' | 'failed';

export function AssistantPage() {
  const { me, upsertChat, touchChat, patchMe, refreshMe } = useApp();
  const { navigate } = useRouter();
  const app = useRef({ upsertChat, touchChat, patchMe, refreshMe }).current;
  const [chat, setChat] = useState<ChatSummary | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [mode, setMode] = useState<'voice' | 'text'>(() => (inAndroid() ? 'voice' : 'text'));
  const [voice, setVoice] = useState<AudioContext | null>(null);
  const [offer, setOffer] = useState<ScreenOffer | null>(null);
  const [screen, setScreen] = useState<ScreenState>('offered');
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const pending = useRef<Attachment[]>([]);
  const saved = useRef(false);
  const live = useLiveTurn(chat?.id ?? '');

  // The sheet's chat: temporary, made as it opens.
  useEffect(() => {
    if (!me) return;
    let gone = false;
    createChat({ projectId: null, model: me.settings.defaultModel, effort: me.settings.defaultEffort, temporary: true })
      .then((c) => {
        markTemporary(c.id);
        if (gone) void deleteChat(c.id).catch(() => undefined);
        else setChat(toSummary(c));
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Assist did not answer.'));
    return () => {
      gone = true;
    };
    // Once, for the sheet.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // What the phone captured, as it arrives.
  useEffect(() => {
    void hello().then((h) => h && setOffer(h.screen));
    return onScreen(setOffer);
  }, []);

  // Voice starts once there is a chat to talk in.
  useEffect(() => {
    if (chat && mode === 'voice' && !voice) setVoice(new AudioContext());
  }, [chat, mode, voice]);

  // The conversation so far, from the Worker once each reply is in.
  useEffect(() => {
    if (!chat || live?.phase !== 'done') return;
    void getChat(chat.id).then((c) => setMessages(c.messages)).catch(() => undefined);
  }, [chat, live?.phase]);

  const discard = useCallback(() => {
    if (chat && !saved.current) {
      stopTurn(chat.id);
      void deleteChat(chat.id).catch(() => undefined);
    }
  }, [chat]);

  const close = useCallback(() => {
    discard();
    void voice?.close().catch(() => undefined);
    if (inAndroid()) closeSheet();
    else navigate('/');
  }, [discard, voice, navigate]);

  const openFull = async () => {
    if (!chat) return;
    try {
      await patchChat(chat.id, { temporary: false });
    } catch {
      setError('That did not work. Try again.');
      return;
    }
    saved.current = true;
    markSaved(chat.id);
    upsertChat({ ...chat, temporary: false });
    if (!(inAndroid() && (await openInApp(`/chat/${chat.id}`)))) navigate(`/chat/${chat.id}`);
  };

  const addScreen = async () => {
    if (!chat) return;
    setScreen('adding');
    const capture = await screenCapture();
    if (!capture) {
      setScreen('failed');
      return;
    }
    try {
      const infos = await Promise.all(captureFiles(capture).map((f) => uploadFile(f, { chatId: chat.id })));
      pending.current = infos.map((i) => ({ fileId: i.id, name: i.name, mime: i.mime, size: i.size }));
      setScreen('added');
    } catch {
      setScreen('failed');
    }
  };

  const takeAttachments = () => {
    const files = pending.current;
    pending.current = [];
    if (files.length) setScreen('offered');
    return files;
  };

  const send = () => {
    const text = draft.trim();
    if (!chat || !text || live?.phase === 'streaming') return;
    setDraft('');
    startTurn({ chat, kind: 'turn', input: { text, attachments: takeAttachments() }, app });
  };

  const thread = withTurn(messages, live);
  const hasReply = thread.some((m) => m.role === 'assistant');

  return (
    <div
      className="mv-app"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
      style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', fontFamily: 'var(--font-body)' }}
    >
      <section
        aria-label="Assist"
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 640,
          height: 'min(72vh, 600px)',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--app-panel)',
          color: 'var(--text-body)',
          borderRadius: '20px 20px 0 0',
          boxShadow: 'var(--shadow-xl)',
          overflow: 'hidden'
        }}
      >
        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 6, padding: '10px 10px 6px 14px' }}>
          {offer && (offer.text || offer.image) ? (
            <button
              type="button"
              className="bare hov-tint"
              disabled={screen === 'adding' || screen === 'added' || !chat}
              onClick={() => void addScreen()}
              style={{
                height: 32,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '0 10px',
                borderRadius: 999,
                border: '1px solid var(--border-subtle)',
                fontSize: 13,
                color: screen === 'added' ? 'var(--primary-text)' : 'var(--text-body)'
              }}
            >
              <Ph name={screen === 'added' ? 'check' : screen === 'adding' ? 'circle-notch' : 'device-mobile'} size={14} className={screen === 'adding' ? 'spin' : undefined} />
              {screen === 'added' ? 'Screen added' : screen === 'failed' ? 'Try the screen again' : 'Add this screen'}
            </button>
          ) : null}
          <span style={{ flex: 1 }} />
          {hasReply ? (
            <button type="button" className="bare hov-tint" onClick={() => void openFull()} style={textButton}>
              Open in Assist
            </button>
          ) : null}
          <button
            type="button"
            className="bare hov-tint"
            aria-label={mode === 'voice' ? 'Type instead' : 'Talk instead'}
            onClick={() => {
              if (mode === 'voice') {
                void voice?.close().catch(() => undefined);
                setVoice(null);
                setMode('text');
              } else setMode('voice');
            }}
            style={iconButton}
          >
            <Ph name={mode === 'voice' ? 'keyboard' : 'waveform'} size={18} />
          </button>
          <button type="button" className="bare hov-tint" aria-label="Close" onClick={close} style={iconButton}>
            <Ph name="x" size={18} />
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
          {error ? (
            <p role="alert" style={{ margin: 0, padding: 20, fontSize: 14, color: 'var(--text-muted)' }}>
              {error}
            </p>
          ) : mode === 'voice' && chat && voice ? (
            <VoiceMode
              chat={chat}
              app={app}
              context={voice}
              takeAttachments={takeAttachments}
              onClose={() => {
                void voice.close().catch(() => undefined);
                setVoice(null);
                setMode('text');
              }}
            />
          ) : (
            <div className="scroll" style={{ position: 'absolute', inset: 0, overflowY: 'auto', padding: '6px 16px 12px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {thread.map((m) =>
                m.body.kind !== 'text' ? null : m.role === 'user' ? (
                  <div
                    key={m.id}
                    style={{
                      alignSelf: 'flex-end',
                      maxWidth: '85%',
                      padding: '9px 13px',
                      borderRadius: 14,
                      background: 'var(--app-panel-sunken)',
                      color: 'var(--text-strong)',
                      fontSize: 15,
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {m.body.text}
                  </div>
                ) : (
                  <div key={m.id} style={{ fontSize: 15, color: 'var(--text-strong)', minWidth: 0 }}>
                    <Markdown text={m.body.text || '…'} />
                  </div>
                )
              )}
            </div>
          )}
        </div>

        {mode === 'text' ? (
          <div style={{ flex: 'none', display: 'flex', alignItems: 'flex-end', gap: 8, padding: '8px 12px max(12px, env(safe-area-inset-bottom))' }}>
            <textarea
              aria-label="Ask Assist"
              placeholder="Ask anything"
              value={draft}
              rows={1}
              autoFocus
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !matchMedia('(pointer: coarse)').matches) {
                  e.preventDefault();
                  send();
                }
              }}
              style={{
                flex: 1,
                minHeight: 40,
                maxHeight: 140,
                resize: 'none',
                border: '1px solid var(--border-subtle)',
                borderRadius: 12,
                padding: '9px 12px',
                background: 'var(--surface-card)',
                color: 'var(--text-strong)',
                fontSize: 15
              }}
            />
            <button
              type="button"
              className="bare"
              aria-label="Send"
              disabled={!draft.trim() || !chat || live?.phase === 'streaming'}
              onClick={send}
              style={{ ...iconButton, width: 40, height: 40, background: 'var(--primary)', color: 'var(--text-on-primary)' }}
            >
              <Ph name="paper-plane-tilt" size={17} />
            </button>
          </div>
        ) : null}
      </section>
    </div>
  );
}

const iconButton = {
  width: 36,
  height: 36,
  flex: 'none',
  display: 'grid',
  placeItems: 'center',
  borderRadius: 'var(--radius-sm)',
  color: 'var(--text-muted)'
} as const;

const textButton = {
  height: 32,
  padding: '0 10px',
  borderRadius: 'var(--radius-sm)',
  fontSize: 13,
  color: 'var(--primary-text)'
} as const;
